import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Field } from 'redux-form';

import ThemedFormFileUpload, {
  FILE_EXISTING_TAG,
} from 'inquiry/components/InputFileUpload/ThemedFormFileUpload';
import { useApply, useValidators } from 'inquiry/lib/jsonLogic';
import { InputFileUploadComponent } from 'lib/interfaces/ui-components';
import { FormValues } from 'lib/utils/formValidation';
import { UiComponentActionsApi } from 'verify/lib/interfaces/uiComponent';
import { Inquiry } from 'verify/models/inquiry';
import { RootState } from 'verify/redux/store';

interface InputFileUploadProps {
  component: InputFileUploadComponent;
  inquiry: Inquiry;
  actionsApi: UiComponentActionsApi;
}

const InputFileUpload: FC<InputFileUploadProps> = ({
  component,
  inquiry,
  actionsApi,
}: InputFileUploadProps) => {
  const {
    disabled,
    prefill,
    hidden,
    label,
    optional,
    validations,
    'file-upload-limit': fileUploadLimit,
    'allowed-file-types': allowedFileTypes,
  } = component.attributes;
  const formField = component.name;
  const apply = useApply(formField, inquiry.attributes['next-step'].name);
  const formValues = useSelector<RootState, FormValues | undefined>(
    (state) => state.form[inquiry.attributes['next-step'].name]?.values,
  );
  const currentValue = formValues && formValues[formField];
  const shouldUseValidations = currentValue !== FILE_EXISTING_TAG;
  const validators = useValidators({
    hidden,
    optional,
    validations: shouldUseValidations ? validations : [],
  });

  const isHidden = apply<boolean>(hidden);

  useEffect(() => {
    if (isHidden) {
      // Setting to '' removes the field value from form state entirely, unlike null which sets value to null.
      actionsApi.setField(formField, '');
    }
  }, [isHidden]);

  if (isHidden) {
    return null;
  }

  return (
    <Field
      id={formField}
      name={formField}
      component={ThemedFormFileUpload}
      disabled={apply<boolean>(disabled)}
      validate={validators}
      label={label}
      fileUploadLimit={fileUploadLimit}
      allowedFileTypes={allowedFileTypes}
      styles={component.styles}
      prefill={prefill}
    />
  );
};

export default InputFileUpload;
